/**
 * Monthly data-point quota: thresholds, projection and copy for the 75% warning
 * banner and the 100% "tracking paused" interrupt (Paper "03 - App · Paywall &
 * billing", Analytics · Event quota 75% / 100%).
 *
 * The allowance is read as a calendar month ("Data points this month"), so it
 * resets on the 1st of the next month. Every number here comes from the
 * account's own `eventsUsed` / `eventLimit`; nothing is invented.
 */
import { formatDataPointAllowance, formatShortDate, getPlanById } from '@/lib/constants/pricing'
import type { PlanTier } from '@/lib/types/billing'

export const QUOTA_WARNING_RATIO = 0.75

export type QuotaLevel = 'ok' | 'warning' | 'full'

export function getQuotaLevel(used: number, limit: number): QuotaLevel {
  if (!Number.isFinite(limit) || limit <= 0) return 'ok'
  if (used >= limit) return 'full'
  if (used / limit >= QUOTA_WARNING_RATIO) return 'warning'
  return 'ok'
}

/** First day of the next calendar month, local time. */
export function getQuotaResetDate(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth() + 1, 1)
}

/**
 * Straight-line projection of when this month's usage reaches the cap, from the
 * average daily rate so far. Null when the cap is already reached, there's no
 * usage to project from, or the pace doesn't reach the cap before the reset.
 */
export function projectQuotaCapDate(used: number, limit: number, now: Date = new Date()): Date | null {
  if (!Number.isFinite(limit) || limit <= 0 || used <= 0 || used >= limit) return null
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  const elapsedDays = Math.max(1, (now.getTime() - monthStart.getTime()) / 86_400_000)
  const perDay = used / elapsedDays
  const capAt = new Date(now.getTime() + ((limit - used) / perDay) * 86_400_000)
  return capAt < getQuotaResetDate(now) ? capAt : null
}

/** The plan every quota and limit CTA points at. Plus is never offered. */
export function getNextPlanTier(tier: PlanTier | string | null | undefined): 'business' | 'scale' | null {
  if (tier === 'scale') return null
  if (tier === 'business') return 'scale'
  return 'business'
}

export function buildQuotaWarningCopy({
  used,
  limit,
  now = new Date(),
}: {
  used: number
  limit: number
  now?: Date
}): { chip: string; message: string } {
  const pct = Math.min(99, Math.floor((used / limit) * 100))
  const reset = formatShortDate(getQuotaResetDate(now))
  const capDate = formatShortDate(projectQuotaCapDate(used, limit, now))
  const lead = `You've used ${formatDataPointAllowance(used)} of ${formatDataPointAllowance(limit)} data points this month.`
  const tail = capDate
    ? ` At this pace you'll reach the cap around ${capDate}, and tracking pauses until ${reset}.`
    : ` Tracking pauses at the cap until ${reset}.`
  return { chip: `${pct}% used`, message: lead + tail }
}

export interface QuotaInterruptCopy {
  title: string
  body: string
  keptHeading: string
  kept: string[]
  pausedHeading: string
  paused: string[]
  cta: { label: string; href?: string } | null
  ctaNote: string | null
}

export function buildQuotaInterruptCopy({
  limit,
  planTier,
  planName,
  siteLabel,
  reachedAt,
  counts,
  now = new Date(),
}: {
  limit: number
  planTier: PlanTier | string
  planName: string
  /** Name the site only when the account tracks one; usage is account-wide. */
  siteLabel?: string | null
  /** Day the cap was reached, when the backend reports it. */
  reachedAt?: Date | string | null
  counts?: { funnels?: number; heatmaps?: number; tests?: number }
  now?: Date
}): QuotaInterruptCopy {
  const cap = formatDataPointAllowance(limit)
  const reset = formatShortDate(getQuotaResetDate(now))
  const reached = formatShortDate(reachedAt)
  const subject = siteLabel ? `${siteLabel} used` : "You've used"

  const tally: string[] = []
  if (counts?.funnels) tally.push(`${counts.funnels} ${counts.funnels === 1 ? 'funnel' : 'funnels'}`)
  if (counts?.heatmaps) tally.push(`${counts.heatmaps} ${counts.heatmaps === 1 ? 'heatmap' : 'heatmaps'}`)
  if (counts?.tests) tally.push(`${counts.tests} ${counts.tests === 1 ? 'A/B test' : 'A/B tests'}`)

  const next = getNextPlanTier(planTier)
  const nextPlan = next ? getPlanById(next) : undefined

  return {
    title: `${subject} all ${cap} data points${reached ? ` on ${reached}` : ' this month'}. New visits aren't being counted.`,
    body: `${planName} includes ${cap} a month. Your allowance resets on ${reset}.`,
    keptHeading: 'Still here',
    kept: [
      reached ? `Everything collected up to ${reached}` : 'Everything collected so far',
      ...(tally.length > 0 ? [tally.join(', ')] : []),
      'Reports, exports and the API',
    ],
    pausedHeading: `Paused until ${reset}`,
    paused: ['New visits and pageviews', 'Clicks and form submissions', 'Heatmap recording'],
    cta: nextPlan
      ? { label: `Upgrade to ${nextPlan.name} · $${nextPlan.monthlyPrice}/mo` }
      : { label: 'Talk to us about Enterprise', href: 'https://humblytics.com/request-demo' },
    ctaNote: nextPlan
      ? `${formatDataPointAllowance(Number(nextPlan.eventLimit))} data points a month. Tracking resumes right away.`
      : null,
  }
}
